import {
  AGREEMENT_IDS,
  DIPLOMACY,
  INVASION,
  LEVERS,
  PROJECTS,
  PROJECT_RULES,
  TRADE_AGREEMENT,
  type AgreementId,
} from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import {
  atWar,
  commandBlocker,
  hasAgreement,
  pairIs,
  routeLevel,
  projectOutcome,
  projectTotal,
  techMods,
  type Command,
  type GameState,
} from '@sd/sim';
import type { Viewer } from '../map/describe';
import { clock, num, playerName, seatColor } from './format';
import { reasonText } from './planetModel';

/**
 * The «Diplomacy» tab (#58): war, peace and agreements with every rival,
 * vassalage both ways, sanctions gathering backers, and the joint projects
 * against crises. Commands are checked against the simulation itself.
 */
export interface DiploAction {
  readonly label: string;
  readonly command: Command | null;
  readonly reason: string | null;
  readonly primary?: boolean;
  readonly heat?: boolean;
  /** A consequence worth a second look before the click. */
  readonly warning?: string;
}

export interface RivalRow {
  readonly id: string;
  readonly name: string;
  readonly faction: string;
  readonly color: string;
  readonly status: string;
  readonly hostile: boolean;
  readonly reputation: number;
  readonly prestige: number;
  readonly tags: readonly string[];
  readonly agreements: readonly { readonly name: string; readonly cancel: Command }[];
  readonly incoming: readonly DiploAction[];
  readonly outgoing: readonly string[];
  readonly actions: readonly DiploAction[];
  readonly sanctions: readonly DiploAction[];
}

export interface ProjectRow {
  readonly id: number;
  readonly name: string;
  readonly crisis: string;
  readonly left: string;
  readonly total: number;
  readonly threshold: number;
  readonly full: number;
  readonly mine: number;
  readonly status: string;
  readonly reward: string;
  readonly parts: readonly { readonly color: string; readonly share: number }[];
  readonly gifts: readonly DiploAction[];
}

export interface DiplomacyView {
  readonly reputation: number;
  readonly standing: string;
  readonly independence: DiploAction | null;
  readonly rivals: readonly RivalRow[];
  readonly projects: readonly ProjectRow[];
}

/** The trade agreement's numbers, for the offer's description. */
const agreementNumbers = (locale: Locale) => ({
  speed: Math.round(TRADE_AGREEMENT.caravanSpeed * 100),
  income: num(locale, TRADE_AGREEMENT.incomeFactor, 1),
  penalty: DIPLOMACY.brokenPactPenalty,
});

/** Goods and science given to a project in one click. */
export const GIVE = 10;

const reason = (locale: Locale, code: string | null) =>
  code ? reasonText(locale, code, undefined, 'diplomacy') : null;

export function diplomacyView(state: GameState, v: Viewer): DiplomacyView | null {
  const { locale, me } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return null;
  const name = (id: string) => playerName(locale, v.setup, id);
  const action = (
    label: string,
    command: Command,
    extra: Partial<DiploAction> = {},
  ): DiploAction => {
    const blocker = commandBlocker(state, command);
    return {
      label,
      command: blocker ? null : command,
      reason: reason(locale, blocker),
      ...extra,
    };
  };

  const rivals = state.players
    .filter((p) => p.id !== me)
    .map((p): RivalRow => {
      const war = atWar(state, me, p.id);
      const pending = state.pendingWars.find((w) => pairIs({ a: w.by, b: w.target }, me, p.id));
      const status = war
        ? t(locale, 'diplomacy.war')
        : pending
          ? pending.by === me
            ? t(locale, 'diplomacy.warInByYou', { time: clock(pending.startsAt - state.tick) })
            : t(locale, 'diplomacy.warIn', {
                time: clock(pending.startsAt - state.tick),
                by: name(pending.by),
              })
          : t(locale, 'diplomacy.peace');
      const tags: string[] = [];
      if (p.vassalOf === me) tags.push(t(locale, 'diplomacy.yourVassal'));
      if (player.vassalOf === p.id) tags.push(t(locale, 'diplomacy.yourOverlord'));
      if (p.vassalOf && p.vassalOf !== me)
        tags.push(t(locale, 'diplomacy.vassalOf', { name: name(p.vassalOf) }));
      for (const s of state.sieges) {
        if (s.owner === me && s.victim === p.id) tags.push(t(locale, 'diplomacy.besieging'));
        if (s.owner === p.id && s.victim === me) tags.push(t(locale, 'diplomacy.besieged'));
      }
      for (const r of state.raids) {
        if (r.owner === p.id && r.victim === me) tags.push(t(locale, 'diplomacy.raidingYou'));
      }
      for (const l of state.levers.filter((x) => x.status === 'active')) {
        if (l.kind === 'embargo' && l.by.includes(me) && l.target === p.id)
          tags.push(t(locale, 'diplomacy.yourEmbargo'));
        if (l.kind === 'embargo' && l.by.includes(p.id) && l.target === me)
          tags.push(t(locale, 'diplomacy.theirEmbargo'));
        if (l.kind === 'sanctions' && l.target === p.id)
          tags.push(t(locale, 'diplomacy.sanctioned'));
      }

      const level = routeLevel(state.agreements, state.tradeRoutes, me, p.id);
      if (level > 0) {
        tags.push(
          t(locale, 'diplomacy.route', {
            level,
            n: state.tradeRoutes.find((r) => pairIs(r, me, p.id))?.deliveries ?? 0,
          }),
        );
      }
      const agreements = state.agreements
        .filter((a) => pairIs(a, me, p.id))
        .map((a) => ({
          name: t(locale, `agreement.${a.kind}.name`),
          cancel: { type: 'cancelAgreement', playerId: me, other: p.id, kind: a.kind } as Command,
        }));
      const incoming = state.proposals
        .filter((x) => x.from === p.id && x.to === me)
        .map((x) =>
          action(
            t(locale, 'diplomacy.accept', { kind: t(locale, `agreement.${x.kind}.name`) }),
            { type: 'accept', playerId: me, other: p.id, kind: x.kind },
            { primary: true },
          ),
        );
      const outgoing = state.proposals
        .filter((x) => x.from === me && x.to === p.id)
        .map((x) =>
          t(locale, 'diplomacy.proposed', { kind: t(locale, `agreement.${x.kind}.name`) }),
        );
      const proposed = (kind: AgreementId | 'peace') =>
        state.proposals.some((x) => x.from === me && x.to === p.id && x.kind === kind);

      const actions: DiploAction[] = [];
      if (war) {
        if (!proposed('peace'))
          actions.push(
            action(
              t(locale, 'diplomacy.offerPeace'),
              { type: 'propose', playerId: me, other: p.id, kind: 'peace' },
              { primary: true },
            ),
          );
      } else if (!pending) {
        for (const kind of AGREEMENT_IDS) {
          if (hasAgreement(state.agreements, me, p.id, kind) || proposed(kind)) continue;
          actions.push(
            action(
              t(locale, 'diplomacy.offer', { kind: t(locale, `agreement.${kind}.name`) }),
              { type: 'propose', playerId: me, other: p.id, kind },
              { warning: t(locale, `diplomacy.does.${kind}`, agreementNumbers(locale)) },
            ),
          );
        }
        const pact = hasAgreement(state.agreements, me, p.id, 'nonAggression');
        actions.push(
          action(
            t(locale, 'diplomacy.declareWar'),
            { type: 'declareWar', playerId: me, target: p.id },
            {
              heat: true,
              warning: pact
                ? t(locale, 'diplomacy.breaksPact', { n: DIPLOMACY.brokenPactPenalty })
                : t(locale, 'diplomacy.warDelay', { time: clock(DIPLOMACY.warDelayTicks) }),
            },
          ),
        );
      }

      const sanctions = state.levers
        .filter(
          (l) =>
            l.kind === 'sanctions' &&
            l.status === 'gathering' &&
            l.target === p.id &&
            !l.by.includes(me),
        )
        .map((l) =>
          action(
            t(locale, 'diplomacy.joinSanctions', {
              by: l.by.map(name).join(', '),
              cost: LEVERS.sanctions.cost,
              time: clock(l.until - state.tick),
            }),
            { type: 'joinSanctions', playerId: me, leverId: l.id },
          ),
        );

      return {
        id: p.id,
        name: name(p.id),
        faction: t(locale, `faction.${p.faction}.name`),
        color: seatColor(p.id),
        status,
        hostile: war || pending !== undefined,
        reputation: p.reputation,
        prestige: p.prestige,
        tags,
        agreements,
        incoming,
        outgoing,
        actions,
        sanctions,
      };
    });

  const overlord = player.vassalOf;
  const vassals = state.players.filter((p) => p.vassalOf === me).map((p) => name(p.id));
  const standing = overlord
    ? t(locale, 'diplomacy.standing.vassal', {
        name: name(overlord),
        pct: Math.round(INVASION.tribute * 100),
      })
    : vassals.length
      ? t(locale, 'diplomacy.standing.overlord', {
          names: vassals.join(', '),
          pct: Math.round(INVASION.tribute * 100),
        })
      : t(locale, 'diplomacy.standing.free');

  const bonus = 1 + techMods(player.techs).projectPrestige;
  const projects = state.projects
    .filter((x) => state.tick < x.deadline)
    .map((x): ProjectRow => {
      const def = PROJECTS[x.kind];
      const crisis = state.crises.find((c) => c.id === x.crisisId);
      const total = projectTotal(x);
      const mine = x.byPlayer[me]?.points ?? 0;
      const outcome = projectOutcome(x);
      const min = def.share * PROJECT_RULES.minCoverShare;
      const gifts: DiploAction[] = [];
      for (const good of def.goods) {
        // Up to ten at a time; with none in stock the button says why.
        const quantity = Math.min(GIVE, Math.floor(player.goods[good])) || GIVE;
        gifts.push(
          action(t(locale, 'project.giveGood', { quantity, good: t(locale, `resource.${good}`) }), {
            type: 'contributeProject',
            playerId: me,
            projectId: x.id,
            what: good,
            quantity,
          }),
        );
      }
      const science = Math.min(GIVE, Math.floor(player.planet.science)) || GIVE;
      gifts.push(
        action(t(locale, 'project.giveScience', { quantity: science }), {
          type: 'contributeProject',
          playerId: me,
          projectId: x.id,
          what: 'science',
          quantity: science,
        }),
      );
      return {
        id: x.id,
        name: t(locale, `project.${x.kind}.name`),
        crisis: crisis ? t(locale, `crisis.${crisis.kind}.name`) : '',
        left: clock(x.deadline - state.tick),
        total,
        threshold: x.threshold,
        full: x.full,
        mine,
        status:
          outcome === 'full'
            ? t(locale, 'diplomacy.project.full')
            : outcome === 'threshold'
              ? mine >= min - 1e-9
                ? t(locale, 'diplomacy.project.covered')
                : t(locale, 'diplomacy.project.notYou', { min: num(locale, min) })
              : t(locale, 'diplomacy.project.short', { n: num(locale, x.threshold - total) }),
        reward: t(locale, 'diplomacy.project.reward', {
          prestige: num(locale, def.prestige * bonus),
          mine: num(locale, total > 0 ? ((def.prestige * mine) / total) * bonus : 0, 1),
        }),
        parts: Object.entries(x.byPlayer)
          .filter(([, c]) => c.points > 0)
          .sort((a, b) => b[1].points - a[1].points)
          .map(([id, c]) => ({ color: seatColor(id), share: Math.min(1, c.points / x.full) })),
        gifts,
      };
    });

  return {
    reputation: player.reputation,
    standing,
    independence: overlord
      ? action(
          t(locale, 'diplomacy.independence'),
          { type: 'declareIndependence', playerId: me },
          { heat: true },
        )
      : null,
    rivals,
    projects,
  };
}
